/**
 * Product Tier Types
 * 
 * Defines the 3-tier product model for FlowState Automate:
 * - Personal: Individual productivity, personal dashboard only
 * - Team: Collaborative workspace with team dashboard (formerly admin)
 * - Self-Hosted: White-label deployment with full data sovereignty
 * 
 * @package @flowstate/types
 * @version 1.0.0
 * @white-label-ready
 */

// ============================================================================
// Product Tier Definitions
// ============================================================================

/**
 * The three product tiers available for FlowState Automate
 */
export type ProductTier = 'personal' | 'team' | 'self_hosted';

/**
 * Deployment model for each tier
 */
export type DeploymentModel = 'cloud' | 'self_hosted';

/**
 * Dashboard type available per tier
 */
export type DashboardType = 'personal' | 'team';

// ============================================================================
// Feature Access Control
// ============================================================================

/**
 * Granular feature identifiers that can be gated by tier
 */
export type TierFeature =
  // Personal features (available in all tiers)
  | 'personal_dashboard'
  | 'todos'
  | 'calendar'
  | 'notifications'
  | 'ai_agent'
  | 'gamification'
  | 'time_tracking'
  | 'profile'
  // Team features (available in team + self_hosted)
  | 'team_dashboard'
  | 'team_management'
  | 'shared_projects'
  | 'team_analytics'
  | 'team_leaderboards'
  | 'team_gamification'
  | 'role_management'
  | 'workflows'
  | 'webhooks'
  | 'multi_org'
  | 'google_workspace_sync'
  | 'marketplace_agents'
  // Self-hosted features (available in self_hosted only)
  | 'custom_branding'
  | 'data_sovereignty'
  | 'sso_integration'
  | 'audit_logs'
  | 'api_access'
  | 'sales_portal'
  | 'accounting_portal'
  | 'xero_integration'
  | 'crm'
  | 'advanced_analytics'
  | 'custom_integrations'
  | 'google_directory_sync';

/**
 * Feature access definition with tier and limit information
 */
export interface TierFeatureAccess {
  /** Whether the feature is available */
  enabled: boolean;
  /** Optional usage limit (e.g., max team members, max projects) */
  limit?: number;
  /** Whether the feature is in beta for this tier */
  beta?: boolean;
}

/**
 * Complete feature access map for a tier
 */
export type TierFeatureMap = Record<TierFeature, TierFeatureAccess>;

// ============================================================================
// Tier Configuration
// ============================================================================

/**
 * Limits applied to each tier
 */
export interface TierLimits {
  /** Maximum number of users (null = unlimited) */
  maxUsers: number | null;
  /** Maximum number of projects (null = unlimited) */
  maxProjects: number | null;
  /** Maximum storage in GB (null = unlimited) */
  maxStorageGB: number | null;
  /** Maximum number of integrations (null = unlimited) */
  maxIntegrations: number | null;
  /** Maximum number of workflows (null = unlimited) */
  maxWorkflows: number | null;
  /** Maximum API calls per month (null = unlimited) */
  maxApiCallsPerMonth: number | null;
  /** Maximum number of team orgs a user can create/own (null = unlimited) */
  maxOrganizations: number | null;
  /** Maximum number of concurrently active marketplace agents (null = unlimited) */
  maxActiveAgents: number | null;
}

/**
 * Full configuration for a product tier
 */
export interface ProductTierConfig {
  /** Tier identifier */
  tier: ProductTier;
  /** Display name for the tier */
  displayName: string;
  /** Short description */
  description: string;
  /** Deployment model */
  deployment: DeploymentModel;
  /** Primary dashboard type */
  dashboardType: DashboardType;
  /** Whether it can be deployed as a web app */
  webAppAvailable: boolean;
  /** Resource limits */
  limits: TierLimits;
  /** Feature access map */
  features: TierFeatureMap;
  /** Pricing information */
  pricing: TierPricing;
}

/**
 * Pricing configuration for a product tier
 */
export interface TierPricing {
  /** Monthly price in USD (per user for team/enterprise, flat for personal) */
  monthlyPriceUsd: number;
  /** Annual price in USD per month (discounted) */
  annualPriceUsd: number;
  /** Whether price is per-user or flat */
  perUser: boolean;
  /** AI credits included per month (null = unlimited for Enterprise tier) */
  includedCredits: number | null;
  /** Cost per additional credit in USD (null = unlimited) */
  additionalCreditCostUsd: number | null;
  /** Free trial duration in days */
  trialDays: number;
}

// ============================================================================
// Tier Context & Resolution
// ============================================================================

/**
 * Current tier context for a user session
 */
export interface TierContext {
  /** The resolved product tier */
  tier: ProductTier;
  /** The organization ID (null for personal tier without org) */
  organizationId: string | null;
  /** Dashboard type to render */
  dashboardType: DashboardType;
  /** Whether the user is on a self-hosted deployment */
  isSelfHosted: boolean;
  /** Feature access for the current tier */
  features: TierFeatureMap;
  /** Resource limits for the current tier */
  limits: TierLimits;
}

/**
 * Result of checking feature access for a tier
 */
export interface TierAccessResult {
  /** Whether the feature is accessible */
  allowed: boolean;
  /** The required tier if not allowed */
  requiredTier?: ProductTier;
  /** Reason the feature is not accessible */
  reason?: string;
  /** Whether the feature is in beta */
  beta?: boolean;
}

// ============================================================================
// Default Tier Configurations
// ============================================================================

/**
 * Personal tier: Individual productivity
 * - Personal dashboard only
 * - Core productivity features
 * - Cloud-hosted web app
 */
export const PERSONAL_TIER_CONFIG: ProductTierConfig = {
  tier: 'personal',
  displayName: 'Starter',
  description: 'Individual productivity and task management for freelancers and solopreneurs',
  deployment: 'cloud',
  dashboardType: 'personal',
  webAppAvailable: true,
  limits: {
    maxUsers: 1,
    maxProjects: 10,
    maxStorageGB: 5,
    maxIntegrations: 3,
    maxWorkflows: 5,
    maxApiCallsPerMonth: 1000,
    maxOrganizations: 0,
    maxActiveAgents: 1,
  },
  pricing: {
    monthlyPriceUsd: 29,
    annualPriceUsd: 24,
    perUser: false,
    includedCredits: 200,
    additionalCreditCostUsd: 0.01,
    trialDays: 14,
  },
  features: {
    // Personal features - all enabled
    personal_dashboard: { enabled: true },
    todos: { enabled: true },
    calendar: { enabled: true },
    notifications: { enabled: true },
    ai_agent: { enabled: true },
    gamification: { enabled: true },
    time_tracking: { enabled: true },
    profile: { enabled: true },
    // Team features - disabled
    marketplace_agents: { enabled: false },
    team_dashboard: { enabled: false },
    team_management: { enabled: false },
    shared_projects: { enabled: false },
    team_analytics: { enabled: false },
    team_leaderboards: { enabled: false },
    team_gamification: { enabled: false },
    role_management: { enabled: false },
    workflows: { enabled: false },
    webhooks: { enabled: false },
    multi_org: { enabled: false },
    google_workspace_sync: { enabled: false },
    // Self-hosted features - disabled
    custom_branding: { enabled: false },
    data_sovereignty: { enabled: false },
    sso_integration: { enabled: false },
    audit_logs: { enabled: false },
    api_access: { enabled: false },
    sales_portal: { enabled: false },
    accounting_portal: { enabled: false },
    xero_integration: { enabled: false },
    crm: { enabled: false },
    advanced_analytics: { enabled: false },
    custom_integrations: { enabled: false },
    google_directory_sync: { enabled: false },
  },
};

/**
 * Team tier: Collaborative workspace
 * - Team dashboard (evolved from admin dashboard)
 * - Team management and collaboration features
 * - Cloud-hosted web app
 */
export const TEAM_TIER_CONFIG: ProductTierConfig = {
  tier: 'team',
  displayName: 'Professional',
  description: 'Collaborative workspace for teams and small businesses',
  deployment: 'cloud',
  dashboardType: 'team',
  webAppAvailable: true,
  limits: {
    maxUsers: 25,
    maxProjects: 50,
    maxStorageGB: 50,
    maxIntegrations: 10,
    maxWorkflows: 25,
    maxApiCallsPerMonth: 10000,
    maxOrganizations: 3,
    maxActiveAgents: 5,
  },
  pricing: {
    monthlyPriceUsd: 49,
    annualPriceUsd: 39,
    perUser: true,
    includedCredits: 1000,
    additionalCreditCostUsd: 0.008,
    trialDays: 14,
  },
  features: {
    // Personal features - all enabled
    personal_dashboard: { enabled: true },
    todos: { enabled: true },
    calendar: { enabled: true },
    notifications: { enabled: true },
    ai_agent: { enabled: true },
    gamification: { enabled: true },
    time_tracking: { enabled: true },
    profile: { enabled: true },
    // Team features - all enabled
    marketplace_agents: { enabled: true },
    team_dashboard: { enabled: true },
    team_management: { enabled: true },
    shared_projects: { enabled: true },
    team_analytics: { enabled: true },
    team_leaderboards: { enabled: true },
    team_gamification: { enabled: true },
    role_management: { enabled: true },
    workflows: { enabled: true },
    webhooks: { enabled: true },
    multi_org: { enabled: true, limit: 3 },
    google_workspace_sync: { enabled: true },
    // Self-hosted features - disabled
    custom_branding: { enabled: false },
    data_sovereignty: { enabled: false },
    sso_integration: { enabled: false },
    audit_logs: { enabled: false },
    api_access: { enabled: true, limit: 10000 },
    sales_portal: { enabled: false },
    accounting_portal: { enabled: false },
    xero_integration: { enabled: false },
    crm: { enabled: true, beta: true },
    advanced_analytics: { enabled: false },
    custom_integrations: { enabled: false },
    google_directory_sync: { enabled: false },
  },
};

/**
 * Self-hosted tier: White-label deployment with full data sovereignty
 * - All features enabled (current app state)
 * - Organization whitelabels app and database
 * - Hosted infrastructure with data isolation
 */
export const SELF_HOSTED_TIER_CONFIG: ProductTierConfig = {
  tier: 'self_hosted',
  displayName: 'Enterprise',
  description: 'White-label deployment with full data sovereignty and customization',
  deployment: 'self_hosted',
  dashboardType: 'team',
  webAppAvailable: true,
  limits: {
    maxUsers: null,
    maxProjects: null,
    maxStorageGB: null,
    maxIntegrations: null,
    maxWorkflows: null,
    maxApiCallsPerMonth: null,
    maxOrganizations: null,
    maxActiveAgents: null,
  },
  pricing: {
    monthlyPriceUsd: 79,
    annualPriceUsd: 69,
    perUser: true,
    includedCredits: null,
    additionalCreditCostUsd: null,
    trialDays: 14,
  },
  features: {
    // Personal features - all enabled
    personal_dashboard: { enabled: true },
    todos: { enabled: true },
    calendar: { enabled: true },
    notifications: { enabled: true },
    ai_agent: { enabled: true },
    gamification: { enabled: true },
    time_tracking: { enabled: true },
    profile: { enabled: true },
    // Team features - all enabled
    marketplace_agents: { enabled: true },
    team_dashboard: { enabled: true },
    team_management: { enabled: true },
    shared_projects: { enabled: true },
    team_analytics: { enabled: true },
    team_leaderboards: { enabled: true },
    team_gamification: { enabled: true },
    role_management: { enabled: true },
    workflows: { enabled: true },
    webhooks: { enabled: true },
    multi_org: { enabled: true },
    google_workspace_sync: { enabled: true },
    // Self-hosted features - all enabled
    custom_branding: { enabled: true },
    data_sovereignty: { enabled: true },
    sso_integration: { enabled: true },
    audit_logs: { enabled: true },
    api_access: { enabled: true },
    sales_portal: { enabled: true },
    accounting_portal: { enabled: true },
    xero_integration: { enabled: true },
    crm: { enabled: true },
    advanced_analytics: { enabled: true },
    custom_integrations: { enabled: true },
    google_directory_sync: { enabled: true },
  },
};

// ============================================================================
// Registry & Helpers
// ============================================================================

/**
 * Registry of all tier configurations
 */
export const PRODUCT_TIER_CONFIGS: Record<ProductTier, ProductTierConfig> = {
  personal: PERSONAL_TIER_CONFIG,
  team: TEAM_TIER_CONFIG,
  self_hosted: SELF_HOSTED_TIER_CONFIG,
};

/**
 * Ordered list of tiers from lowest to highest
 */
export const TIER_HIERARCHY: readonly ProductTier[] = ['personal', 'team', 'self_hosted'] as const;

/**
 * Get the tier config for a given tier
 */
export function getProductTierConfig(tier: ProductTier): ProductTierConfig {
  return PRODUCT_TIER_CONFIGS[tier];
}

/**
 * Check if a feature is available for a given tier
 */
export function isTierFeatureEnabled(tier: ProductTier, feature: TierFeature): boolean {
  const config = PRODUCT_TIER_CONFIGS[tier];
  return config.features[feature]?.enabled ?? false;
}

/**
 * Check if a tier meets or exceeds the required tier level
 */
export function isTierSufficient(currentTier: ProductTier, requiredTier: ProductTier): boolean {
  const currentIndex = TIER_HIERARCHY.indexOf(currentTier);
  const requiredIndex = TIER_HIERARCHY.indexOf(requiredTier);
  return currentIndex >= requiredIndex;
}

/**
 * Get the minimum tier required for a feature
 */
export function getMinimumTierForFeature(feature: TierFeature): ProductTier {
  for (const tier of TIER_HIERARCHY) {
    if (PRODUCT_TIER_CONFIGS[tier].features[feature]?.enabled) {
      return tier;
    }
  }
  return 'self_hosted';
}

/**
 * Check feature access and return a detailed result
 */
export function checkTierAccess(tier: ProductTier, feature: TierFeature): TierAccessResult {
  const config = PRODUCT_TIER_CONFIGS[tier];
  const featureAccess = config.features[feature];
  
  if (featureAccess?.enabled) {
    return { 
      allowed: true,
      beta: featureAccess.beta,
    };
  }

  const requiredTier = getMinimumTierForFeature(feature);
  return {
    allowed: false,
    requiredTier,
    reason: `This feature requires the ${PRODUCT_TIER_CONFIGS[requiredTier].displayName} plan or higher`,
  };
}
